// Photosafety governor for full-screen flashes (ported from the parallel
// build's render/flash.ts): any flash covering most of the framebuffer stays
// under 0.35 luminance-equivalent intensity and 120 ms, and no more than 3
// may start inside a rolling 1 s window. The rate cap REFUSES a fourth
// request outright rather than shortening or queueing it; reduced-flashes
// mode tightens all three bounds to 0.15 / 60 ms / 1 per second.
//
// Pure and deterministic: the caller supplies the clock (`nowS`) from the
// same source the call site animates on (the sim tick for storm visuals), so
// replays, pause, and time dilation behave identically to gameplay. An
// in-bounds request at an infrequent cadence passes through unchanged.

const MAX_LUMINANCE = 0.35;
const MAX_DURATION_S = 0.12;
const MAX_RATE_PER_S = 3;

const REDUCED_LUMINANCE = 0.15;
const REDUCED_DURATION_S = 0.06;
const REDUCED_RATE_PER_S = 1;

const WINDOW_S = 1.0;

export class FlashGovernor {
  /** Reduced-flashes accessibility toggle; reuse the renderer's existing flag. */
  reduced = false;

  /** Ring of recent admission timestamps for the rolling-window rate check. */
  private readonly stamps = new Float64Array(MAX_RATE_PER_S).fill(-WINDOW_S * 4);
  private stampCursor = 0;
  private admittedLuminance = 0;
  private admittedDurationS = 0;

  /** Admitted intensity of the last accepted request (clamped into bounds). */
  get luminance(): number {
    return this.admittedLuminance;
  }

  /** Admitted duration of the last accepted request, seconds (clamped). */
  get durationS(): number {
    return this.admittedDurationS;
  }

  /**
   * Requests a full-screen flash at `nowS` (seconds). Returns false when the
   * rolling-window rate cap refused it; on admission the intensity/duration
   * are clamped into the bounds and readable via the getters.
   */
  request(nowS: number, luminance: number, durationS: number): boolean {
    const rate = this.reduced ? REDUCED_RATE_PER_S : MAX_RATE_PER_S;
    let used = 0;
    for (let i = 0; i < rate; i++) {
      if (nowS - this.stamps[i] < WINDOW_S) used++;
    }
    if (used >= rate) return false;

    this.stamps[this.stampCursor] = nowS;
    this.stampCursor = (this.stampCursor + 1) % this.stamps.length;

    this.admittedLuminance = Math.min(luminance, this.reduced ? REDUCED_LUMINANCE : MAX_LUMINANCE);
    this.admittedDurationS = Math.min(durationS, this.reduced ? REDUCED_DURATION_S : MAX_DURATION_S);
    return true;
  }

  /** Clears the rolling window (call when the clock source restarts). */
  reset(): void {
    this.stamps.fill(-WINDOW_S * 4);
    this.stampCursor = 0;
  }
}
